/** Sending a document: declare it, put the bytes where the server says, then confirm — ADR-0058, A2.
 *
 * Three requests where there used to be one, and the reason is arithmetic rather than taste. The
 * API runs on Lambda, whose real request ceiling is 4.5 MiB against a promised 25 MiB (measured
 * 2026-10-16), so a document does not travel through it. The browser **declares** the file
 * (`POST /evidence/uploads`, which carries no body and so always reaches the application and can
 * refuse on size with DOC-01's remedy), **puts** the bytes at the landing bucket with a URL the
 * server signed, and then **confirms** (`POST …/complete`), at which point the server reads what
 * arrived, hashes it, validates it and makes the document.
 *
 * **What this file knows, and what it is careful not to.**
 *
 * - It holds **no number**. The limit is the server's (`GET /evidence/limits`); the declaration is
 *   refused over it by the server too, so a client that skipped the pre-check still meets the
 *   careful refusal.
 * - It holds **no list of headers**. What a presigned PUT must carry — the content type, the two
 *   encryption headers, the conditional write — is a fact about botocore and the bucket policy, so
 *   the server returns the exact set and this sends it. A header added here would be one S3 ignores
 *   or, worse, one the bucket's CORS rule does not allow, which a browser reports as an opaque
 *   network error.
 * - It sends **a checksum if it can, and carries on if it cannot**. The file's SHA-256 goes in the
 *   declaration (`sha256`), where the server signs it into the upload so S3 refuses a damaged body,
 *   and looks up whether it already holds the file *before* the bytes move. It is a claim and not
 *   the record: the digest on the document is the server's own, computed from what it reads back,
 *   so nothing here is trusted because this computed it. Hashing is best effort — an old browser,
 *   an insecure context or a read failure sends no checksum, and the upload is exactly what it was.
 * - It will **only PUT to a place the server could legitimately name**: a path on this API (the
 *   development stand-in for S3) or one regional landing-bucket origin (`isLandingOrigin`). The CSP
 *   would stop anything else, but a check that fails with a sentence is better than a policy
 *   violation in the console, and a URL from a response is input.
 *
 * **Why confirming is safe to repeat.** The server stores the outcome of a claim on the intent, so a
 * second `complete` returns the document the first one made (`already_completed`) and a duplicate or
 * other refusal is replayed. That is the property the three failure shapes below depend on: a
 * confirm that times out at a proxy can simply be sent again, which for the old single request
 * would have been the way to double a figure.
 */

import { api, apiUrl } from "./client";
import { isLandingOrigin } from "./origin";
import { Refused, refusalFrom } from "./refusal";
import type { components } from "./schema";

type DocumentKind = components["schemas"]["DocumentKind"];
type UploadTarget = components["schemas"]["UploadTargetOut"];
/** The document a confirmed upload made. `accepted` is false for a quarantined one — received,
 * stored, and not usable — and `already_completed` is true when an earlier confirm made it. */
export type Uploaded = components["schemas"]["ClaimedUploadOut"];

/** Where the work has got to, for a progress display. `sent` and `total` are bytes. */
export interface UploadProgress {
  step: "declaring" | "sending" | "confirming";
  sent: number;
  total: number;
}

/** The file is already held: the one upload refusal with a next step of its own (look at what is
 * there), so it is a type a screen can branch on rather than a sentence. */
export class DuplicateUploadError extends Error {
  constructor(
    message: string,
    readonly existingDocumentId: string,
  ) {
    super(message);
    this.name = "DuplicateUploadError";
  }
}

/** The upload stopped part-way, and **which part decides what a retry does**.
 *
 * - `send`: the bytes did not get there (or the link had died). Nothing was kept and no document
 *   exists, so the retry starts again from a fresh declaration.
 * - `confirm`: the bytes got there, or may have, and the confirmation did not come back. The intent
 *   is known (`intentId`) and confirming is idempotent, so the retry **confirms the same intent
 *   again** — it does not send the file a second time, and it cannot add the document twice.
 *
 * Distinct from `Refused`, deliberately. A refusal is the server saying no, and retrying it is
 * useless; this is the network or the platform interrupting, and retrying is the remedy.
 */
export class UploadInterrupted extends Error {
  constructor(
    message: string,
    readonly stage: "send" | "confirm",
    /** Empty until a declaration succeeded; only `confirm` carries one a retry can use. */
    readonly intentId: string,
    /** The HTTP status that interrupted it, or `0` when no response arrived at all. */
    readonly status: number,
  ) {
    super(message);
    this.name = "UploadInterrupted";
  }
}

/** The server's own words for a refusal that carried a body, or `""`. */
function detailOf(error: unknown): {
  error: string;
  message: string;
  existing: string;
} {
  const none = { error: "", message: "", existing: "" };
  if (!error || typeof error !== "object" || !("detail" in error)) return none;
  const detail = (error as { detail?: unknown }).detail;
  if (!detail || typeof detail !== "object") return none;
  const body = detail as { error?: unknown; message?: unknown; existing_document_id?: unknown };
  return {
    error: typeof body.error === "string" ? body.error : "",
    message: typeof body.message === "string" ? body.message : "",
    existing: typeof body.existing_document_id === "string" ? body.existing_document_id : "",
  };
}

/** Lowercase hex of the SHA-256 of some bytes - the form the server stores and compares. */
export async function sha256OfBytes(bytes: Uint8Array): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes as BufferSource);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** The file's SHA-256 as hex, or `null` when it cannot be had. **Never throws**: this is a
 * convenience that must not be able to stop an upload (see the module comment). `crypto.subtle`
 * exists only in secure contexts, so its absence is an ordinary answer and not an error, and a file
 * that cannot be read into memory is left for the PUT to read — which streams it. */
export async function sha256Hex(file: Blob): Promise<string | null> {
  try {
    if (!globalThis.crypto?.subtle) return null;
    // Copied into a typed array of this realm: `subtle.digest` rejects a buffer from another one,
    // which is what a test environment's `Blob` hands back and is harmless in a browser.
    return await sha256OfBytes(new Uint8Array(await file.arrayBuffer()));
  } catch {
    return null;
  }
}

const LOCAL_TARGET = /^\/api\/v1\/evidence\/uploads\/[0-9a-f-]{36}\/object$/;

/** Refuse a destination the server could not legitimately have named. Exported for its test. */
export function assertPermittedTarget(url: string): void {
  if (url.startsWith("/")) {
    if (LOCAL_TARGET.test(url)) return;
  } else {
    try {
      if (isLandingOrigin(new URL(url).origin)) return;
    } catch {
      // Not a URL at all: falls through to the refusal below.
    }
  }
  throw new Refused(
    0,
    "upload_target_refused",
    "EmitIQ was told to send the file somewhere it does not send files, so nothing was sent.",
  );
}

/** PUT the bytes, reporting progress. Resolves on a 2xx — and on a 412.
 *
 * **`412 Precondition Failed` is success, and that is the whole point of the conditional write.**
 * The URL's PUT carries `If-None-Match: *`, so a second PUT to the same key is refused. The only
 * way this client makes a second PUT is a retry after a response it never saw, and then the object
 * is already there — which is what it was trying to achieve. Treating it as a failure would turn a
 * successful send into an error the customer has to retry for ever.
 *
 * `XMLHttpRequest` rather than `fetch`, for one reason: `fetch` cannot report upload progress, and
 * a 25 MiB send on an ordinary connection takes long enough that a silent screen reads as broken.
 *
 * **Credentials only for a path on this API.** The landing bucket's CORS rule does not allow
 * credentialed requests, a presigned URL needs none, and sending the session cookie to a host that
 * is not ours would be wrong even if it were allowed.
 */
function putBytes(
  target: UploadTarget,
  file: File,
  onProgress: (sent: number, total: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const local = target.url.startsWith("/");
    const request = new XMLHttpRequest();
    request.open(target.method, local ? apiUrl(target.url) : target.url);
    request.withCredentials = local;
    for (const [name, value] of Object.entries(target.headers)) {
      request.setRequestHeader(name, value);
    }
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded, event.total);
    };
    request.onload = () => {
      if ((request.status >= 200 && request.status < 300) || request.status === 412) resolve();
      else reject(new PutRefused(request.status));
    };
    request.onerror = () => reject(new PutRefused(0));
    request.onabort = () => reject(new PutRefused(0));
    request.send(file);
  });
}

class PutRefused extends Error {
  constructor(readonly status: number) {
    super(`the upload was refused with status ${status}`);
  }
}

export interface UploadRequest {
  file: File;
  kind: string;
  /** Optional: the site the file is about, as the uploader says. A hint, not a match. */
  facilityId?: string;
  allowDuplicate?: boolean;
  /** Set to confirm an intent the bytes are already at, instead of declaring and sending again. */
  resumeIntentId?: string;
  onProgress?: (progress: UploadProgress) => void;
  /** How the file's checksum is had. The default hashes in the browser; a test passes its own, and
   * a caller that wants none can pass one that resolves `null`. */
  checksum?: (file: File) => Promise<string | null>;
}

/** Declare, send, confirm. See the module comment; the three failure shapes are the contract.
 *
 * Throws `Refused` for a refusal (retrying is useless), `DuplicateUploadError` for a duplicate,
 * and `UploadInterrupted` for the interruptions a retry fixes.
 */
export async function uploadDocument(request: UploadRequest): Promise<Uploaded> {
  const { file, onProgress } = request;
  const report = (step: UploadProgress["step"], sent: number) =>
    onProgress?.({ step, sent, total: file.size });

  let intentId = request.resumeIntentId ?? "";
  if (!intentId) {
    report("declaring", 0);
    // Inside the "declaring" step: it is the same wait to the person, and a hash of 25 MiB is a
    // fraction of a second. A claim — see the module comment — and absent is fine.
    const sha256 = await (request.checksum ?? sha256Hex)(file);
    let declared;
    try {
      declared = await api.POST("/api/v1/evidence/uploads", {
        body: {
          filename: file.name,
          content_type: file.type,
          byte_size: file.size,
          // Left out, not sent as `null`, when there is none: an optional field the server never
          // hears about is one its absence cannot be confused with.
          ...(sha256 ? { sha256 } : {}),
          // The server validates the enum and answers 422 for a value it does not have; the screen's
          // picker offers only the ones it does.
          kind: request.kind as DocumentKind,
          allow_duplicate: request.allowDuplicate ?? false,
          // The uploader's site, a **hint** (SEC-05: nothing a document says is resolved to a row, and
          // neither is this - the reviewer still chooses the meter). Left out when none was named.
          ...(request.facilityId ? { facility_hint_id: request.facilityId } : {}),
        },
      });
    } catch {
      throw new UploadInterrupted(
        `EmitIQ could not be reached, so ${file.name} was not sent. Nothing was kept.`,
        "send",
        "",
        0,
      );
    }
    if (declared.error || !declared.data) {
      // **A duplicate found from the checksum, before a byte moved.** The same refusal the claim
      // would have made after the transfer, with the same next step, and it is what the checksum is
      // for: the alternative is sending 25 MiB to be told the same thing.
      const known = detailOf(declared.error);
      if (known.error === "duplicate" && known.existing) {
        throw new DuplicateUploadError(known.message, known.existing);
      }
      const refusal = refusalFrom(
        declared.response,
        declared.error,
        "The upload could not be started.",
      );
      // A platform answer (502, 503, a timeout) at this step means no intent was recorded as far
      // as anyone can tell, and even if one was, it is harmless: it expires with nothing kept. So
      // this is an interruption that a fresh declaration fixes, and **not** the "it is not known
      // whether that was saved" sentence a bare ambiguous refusal would print — nothing here has
      // been saved yet.
      // (A 503 the application wrote itself - `uploads_unavailable` - is no longer "ambiguous" to
      // `Refused`, because its sentence is certain; here it still means "start again", so it is
      // named: it is the same interruption with a better sentence.)
      if (refusal.isAmbiguous || refusal.status === 503) {
        throw new UploadInterrupted(refusal.message, "send", "", refusal.status);
      }
      throw refusal;
    }
    intentId = declared.data.intent_id;
    assertPermittedTarget(declared.data.upload.url);

    report("sending", 0);
    try {
      await putBytes(declared.data.upload, file, (sent) => report("sending", sent));
    } catch (error) {
      const status = error instanceof PutRefused ? error.status : 0;
      throw new UploadInterrupted(
        status === 403
          ? `The link to send ${file.name} had expired or was refused, so it was not sent. Nothing was kept.`
          : `${file.name} could not be sent. Nothing was kept.`,
        "send",
        intentId,
        status,
      );
    }
  }

  report("confirming", file.size);
  let confirmed;
  try {
    confirmed = await api.POST("/api/v1/evidence/uploads/{intent_id}/complete", {
      params: { path: { intent_id: intentId } },
    });
  } catch {
    throw new UploadInterrupted(
      "EmitIQ could not confirm that it received the file. Nothing is lost: confirming again is safe and cannot add it twice.",
      "confirm",
      intentId,
      0,
    );
  }
  if (confirmed.data) return confirmed.data;

  const detail = detailOf(confirmed.error);
  if (detail.error === "duplicate" && detail.existing) {
    throw new DuplicateUploadError(detail.message, detail.existing);
  }
  if (detail.error === "not_uploaded") {
    // The server looked and nothing is there: the send never landed. A new declaration, then.
    throw new UploadInterrupted(
      `${file.name} did not arrive, so nothing was kept.`,
      "send",
      intentId,
      confirmed.response.status,
    );
  }
  const refusal = refusalFrom(confirmed.response, confirmed.error, "The upload was refused.");
  if (refusal.isAmbiguous || refusal.status === 503) {
    throw new UploadInterrupted(refusal.message, "confirm", intentId, refusal.status);
  }
  throw refusal;
}
